import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ReCAPTCHA from 'react-google-recaptcha'
import isEmpty from 'lodash/isEmpty'

import { Filter, ValidationErrorCode } from 'cat-shared/utils'
import { DEFAULT_THEME, FormName, RegisterFormValues } from 'client/utils'
import { useEnvContext, useAppContext, usePageStateContext, usePage } from 'client/hooks'
import { useRegisterUser } from 'client/api'

import {
  getLocalLastUserConfig,
  parseApiErrors,
  startUserSocket,
  displayFormValidationError,
} from 'client/services'

import {
  AppButton,
  AppDivider,
  AppForm,
  AppGroup,
  AppMessageBox,
  AppParagraph,
} from 'client/ui-building-blocks'

import {
  NewEmailInput,
  NewUsernameInput,
  DateOfBirthPicker,
  CountryAutocomplete,
  SexCheckbox,
} from 'client/components'

import PasswordInput from './fields/password-input'

import styles from './register-form.module.scss'

const RegisterForm = () => {
  const { t, i18n } = useTranslation()

  const [apiErrors, setApiErrors] = useState(null)
  const [recaptchaSelected, setRecaptchaSelected] = useState<boolean>(false)

  const { setAuthUser } = useAppContext()
  const { recaptchaSiteKey } = useEnvContext()
  const { navigateTo } = usePage()

  const {
    setBrowseQuestionsPageState,
    setRedirectQuestionId,
    redirectQuestionId,
    setAuthForm,
    setRandomizerCategory,
  } = usePageStateContext()

  const form = useForm<RegisterFormValues, undefined, RegisterFormValues>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      username: '',
      password: '',
      dateOfBirth: null,
      country: '',
      sex: '',
    },
  })

  const {
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = form

  const { mutateAsync: register, isLoading: isRegistering } = useRegisterUser({
    onSuccess: data => {
      if (data.user.config.confirmed) {
        setBrowseQuestionsPageState(state => ({ ...state, filter: Filter.Answered }))
      }

      setAuthUser(data.user)
      startUserSocket(data.user._id.toString())
      setRandomizerCategory('any')

      if (redirectQuestionId) {
        navigateTo({ path: '/question', query: { _id: redirectQuestionId } })
        setRedirectQuestionId(null)
      } else {
        navigateTo({ path: '/profile', query: { _id: data.user._id.toString() } })
      }
    },
    onError: err => {
      if (err.valErr) {
        Object.keys(err.valErr).forEach(fieldName => {
          displayFormValidationError(
            fieldName,
            null,
            err.valErr[fieldName].message as ValidationErrorCode
          )
        })
      }
    },
  })

  const onClearClick = () => {
    reset()
    setApiErrors(null)
  }

  const onLoginClick = () => {
    setAuthForm(FormName.AuthenticationForm)
    window.scrollTo(0, 0)
  }

  const onRecaptchaChange = () => {
    setRecaptchaSelected(!recaptchaSelected)
  }

  const onSubmit = handleSubmit(
    data => {
      return register({
        ...data,
        dateOfBirth: new Date(data.dateOfBirth).toISOString().split('T')[0],
      }).catch(err => {
        if (err?.valErr) {
          setApiErrors(parseApiErrors(err.valErr))
        }
      })
    },
    errors => {
      if (errors) {
        Object.keys(errors).forEach(fieldName => {
          displayFormValidationError(fieldName, errors[fieldName].message)
        })
      }
    }
  )

  const currentTheme = getLocalLastUserConfig().theme || DEFAULT_THEME

  return (
    <>
      <AppMessageBox paragraphs={[t('authPage.register.info.description')]} />
      <AppDivider />
      <FormProvider {...form}>
        <AppForm onSubmit={onSubmit}>
          <NewEmailInput isLoading={isRegistering} apiErrors={apiErrors} />
          <NewUsernameInput isLoading={isRegistering} apiErrors={apiErrors} />
          <PasswordInput isLoading={isRegistering} />
          <DateOfBirthPicker isLoading={isRegistering} />
          <CountryAutocomplete isLoading={isRegistering} />
          <SexCheckbox isLoading={isRegistering} />
          <AppDivider border />
          <div className={styles.recaptchaContainer}>
            <ReCAPTCHA
              sitekey={recaptchaSiteKey}
              theme={currentTheme}
              hl={i18n.language}
              onChange={onRecaptchaChange}
            />
          </div>
          <AppGroup fullWidth="mobile" alignment="verticalMobile">
            <AppButton
              variant="info"
              text={t('common.buttons.createAnAccount')}
              onClick={onSubmit}
              loading={isRegistering}
              disabled={!recaptchaSelected}
            />
            <AppButton
              variant="blank"
              type="reset"
              text={t('common.buttons.clear')}
              onClick={onClearClick}
              disabled={isRegistering || (!isDirty && isEmpty(errors))}
            />
          </AppGroup>
        </AppForm>
      </FormProvider>
      <AppDivider border size="large" />
      <AppParagraph
        text={t('authPage.register.alreadyHaveAnAccount')}
        centered
        size="small"
        marginBottom="xsmall"
      />
      <AppButton
        variant="success"
        text={t('authPage.register.login')}
        onClick={onLoginClick}
        position="center"
      />
    </>
  )
}

export default RegisterForm
